/**
 * Magic-link landing page.
 *
 * Mounted at /r/:token. Calls the bridge's /api/bridge/magic/redeem
 * endpoint, stores the returned session payload in sessionStorage, and
 * redirects to /bridge/onboard. On failure, shows a clear "this link
 * doesn't work" page — never echoes the token back into the UI.
 */

import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { redeemMagicLink, setBridgeSession } from "../../lib/bridgeClient";

export default function BridgeLanding() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [renewable, setRenewable] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setError("This link is missing its token.");
      return;
    }

    // The symmetric PDF/fields-schema key arrives in the URL fragment.
    // Fragments are never transmitted in HTTP requests, so the bridge
    // never sees this. The SPA reads it locally and stashes it in
    // sessionStorage alongside the bridge session.
    const symmetricKeyB64 =
      typeof window !== "undefined" && window.location.hash.startsWith("#")
        ? window.location.hash.slice(1)
        : null;

    redeemMagicLink(token)
      .then((payload: any) => {
        if (cancelled) return;
        setBridgeSession({
          sessionToken: payload.session_token,
          assignmentId: payload.assignment_id,
          formTitle: payload.form_title,
          pubkeyB64: payload.pubkey_b64,
          // The expected hashes are NOT stored, because redeem no longer
          // returns them: the comparison moved to the bridge
          // (POST /api/bridge/magic/verify-identity, case imp_bad9596e).
          // Handing the answer to the browser made the check cosmetic, and an
          // unsalted sha256 of a date of birth is ~36,500 candidates, so it
          // also gave away the date it was protecting.
          identityFactors: payload.identity_factors || null,
          // Whether this session must pass the challenge before it can fetch
          // anything. Replaces "is there a hash present?" as the signal.
          identityRequired: !!payload.identity_required,
          // Split-link two-signer: the bridge tells us which signer slot
          // this magic link is for ("patient" / "plan_member" / "legacy")
          // and which canonical fields THIS signer is allowed to fill.
          // Downstream wizard uses these to (a) skip the patient-profile
          // pages for the plan-member role and (b) render a role banner.
          role: payload.role || "legacy",
          fieldNames: payload.field_names || null,
          symmetricKeyB64: symmetricKeyB64 as string | undefined,
          // Optional note from the physician — rendered on the onboarding page
          // before the welcome copy. Never echoed in SMS/email body.
          physicianNote: payload.physician_note || null,
          // The physician's own preview of the link: the page says so, and the
          // Mac acknowledges the submission without applying it.
          selfTest: !!payload.self_test,
          // When the link stops working, for the "Expires in N days" line.
          linkExpiresAt: payload.expires_at || null,
          // The patient's fee-only link (G23(f)): the fee step and nothing
          // else (the bridge refuses the form itself to this slot).
          feeOnly: !!payload.fee_only,
        });
        // Strip the fragment from the URL so a casual peek at the address
        // bar (or a browser-history dump) won't include the key.
        if (typeof window !== "undefined" && window.history?.replaceState) {
          window.history.replaceState(
            {},
            "",
            window.location.pathname + window.location.search
          );
        }
        // Coverage-inquiry links land on the coverage question page — no
        // PDF or signing wizard behind them (mirrors the status routing).
        if (payload.purpose === "coverage") {
          navigate("/bridge/coverage", { replace: true });
          return;
        }
        // Status-purpose links (and any link whose assignment has already
        // been submitted) land on the tracker; everything else enters the
        // signing wizard.
        const wantsStatus =
          searchParams.get("dest") === "status" ||
          payload.purpose === "status" ||
          ["signed", "acked"].includes(payload.assignment_status || "");
        // Prescriber signing links (Model A) go to the review-and-sign page,
        // not the patient wizard: a different gate (PIN, not date of birth),
        // a different payload (the completed form, no fields-schema and no
        // profile), and a signer who must never write to a patient's file.
        if (!wantsStatus && payload.role === "prescriber") {
          navigate("/bridge/prescriber", { replace: true });
          return;
        }
        navigate(wantsStatus ? "/bridge/status" : "/bridge/onboard", { replace: true });
      })
      .catch((err: any) => {
        if (cancelled) return;
        // Renewable expiry (bridge keeps the token row for 90 days): offer
        // the self-serve fresh-link page instead of a dead end.
        const detail = String(err?.detail || err?.message || "");
        if (err?.status === 401 && detail.includes('"expired"')) {
          setRenewable(true);
          setError(
            "This link has lapsed — links work for 72 hours after they're sent. You can get a fresh one right now, or contact your clinician's office for a new link."
          );
        } else {
          setError(
            "This link is no longer available. Links lapse 7 days after they're first sent — please contact your clinician's office for a new link."
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [token, navigate]);

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f5f0eb",
        padding: 24,
        textAlign: "center",
      }}
    >
      {!error ? (
        <>
          <h2 style={{ color: "#182a33", marginBottom: 8 }}>Opening your form…</h2>
          <p style={{ color: "#5b6e7c" }}>One moment.</p>
        </>
      ) : (
        <>
          <h2 style={{ color: "#c25450", marginBottom: 12 }}>Link not available</h2>
          <p style={{ color: "#5b6e7c", maxWidth: 420, lineHeight: 1.5 }}>{error}</p>
          {renewable && (
            <button
              onClick={() =>
                navigate(
                  `/bridge/renew?t=${encodeURIComponent(token as string)}${window.location.hash || ""}`
                )
              }
              style={{
                marginTop: 16, padding: "11px 22px", borderRadius: 10,
                border: "none", background: "#1b5e6e", color: "#fff",
                fontSize: 15, fontWeight: 600, cursor: "pointer",
              }}
            >
              Get a fresh link
            </button>
          )}
        </>
      )}
    </div>
  );
}
